import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { createAppKit } from '@reown/appkit/react';
import { UniversalProvider } from '@walletconnect/universal-provider';

export const XRPLWalletContext = createContext(null);

const projectId = process.env.REACT_APP_REOWN_PROJECT_ID || "YOUR_PROJECT_ID";

/**
 * 1. Define the Custom Network (InferredCaipNetwork)
 * Strictly following your documentation's Sui/Cosmos pattern.
 */
const xrplMainnet = {
  id: 1, 
  chainNamespace: 'xrpl',
  caipNetworkId: 'xrpl:0',
  name: 'XRPL Mainnet',
  nativeCurrency: { name: 'XRP', symbol: 'XRP', decimals: 6 },
  rpcUrls: { default: { http: ['https://s1.ripple.com:51234'] } },
  explorerUrls: { default: { url: 'https://livenet.xrpl.org' } }
};

const metadata = {
  name: "Sonar Muse",
  description: "A Web3 Music Store and Streaming Platform",
  url: window.location.origin,
  icons: ["https://sonarmuse.org/favicon.ico"],
};

/**
 * 2. Configure AppKit Core
 * We use manualWCControl: true to allow us to pass the URI manually,
 * which is the official path for Custom Networks.
 */
const modal = createAppKit({
  projectId,
  networks: [xrplMainnet],
  manualWCControl: true, 
  metadata,
  features: { analytics: true },
  // This ensures the modal doesn't try to filter by "officially supported" 
  // which is what causes the grey-out.
  enableInjected: true,
});

export function XRPLWalletProvider({ children }) {
  const [provider, setProvider] = useState(null);
  const [modal, setModal] = useState(null);
  const [session, setSession] = useState(null);
  const [address, setAddress] = useState(null);

  // Initialize UniversalProvider and AppKit
  useEffect(() => {
    async function init() {
      try {
        const universalProvider = await UniversalProvider.init({
          projectId,
          metadata,
        });

        const appKit = createAppKit({
          projectId,
          networks: [xrplMainnet],
          universalProvider: universalProvider,
          manualWCControl: true, // Required to manually pass the URI to the modal
          metadata,
          features: { analytics: true },
        });

        // Listen to display_uri event and feed modal with uri
        universalProvider.on('display_uri', (uri) => {
          appKit.open({ uri });
        });

        // Auto-restore session if it exists
        if (universalProvider.session) {
          setSession(universalProvider.session);
          const acc = universalProvider.session.namespaces?.xrpl?.accounts[0]?.split(":")[2];
          setAddress(acc);
        }

        setProvider(universalProvider);
        setModal(appKit);
      } catch (err) {
        console.error("Initialization Error:", err);
      }
    }
    init();
  }, []);

const connect = useCallback(async () => {
    if (!provider || !modal) return;

    try {
      // Show spinner/modal immediately
      modal.open();

      // Connect provider - this triggers the 'display_uri' event defined in useEffect
      const newSession = await provider.connect({
        optionalNamespaces: {
          xrpl: {
            methods: ["xrpl_signAndSubmit", "xrpl_signTransaction", "signMessage"],
            chains: [xrplMainnet.caipNetworkId],
            events: ["accountsChanged", "chainChanged"]
          },
        }
      });

      if (newSession) {
        setSession(newSession);
        const acc = newSession.namespaces?.xrpl?.accounts[0]?.split(":")[2];
        setAddress(acc);
      }
      
      modal.close();
    } catch (err) {
      console.error("Connection failed:", err);
      modal.close();
    }
  }, [provider, modal]);

  const disconnect = useCallback(async () => {
    if (!provider || !session) return;
    try {
      await provider.disconnect();
    } catch (e) {
      console.warn("Disconnect error:", e);
    }
    setSession(null);
    setAddress(null);
  }, [provider, session]);

  const value = useMemo(() => ({
    connect,
    disconnect,
    connected: !!session,
    address,
    setAddress,
    session,
    setSession,
    client: provider,
    account: address, 
    modal 
  }), [connect, disconnect, address, session, provider, modal]);

  return (
    <XRPLWalletContext.Provider value={value}>
      {children}
    </XRPLWalletContext.Provider>
  );
}

export function useXRPLWalletConnect() {
  const ctx = useContext(XRPLWalletContext);
  if (!ctx) throw new Error("useXRPLWalletConnect must be used inside XRPLWalletProvider");
  return ctx;
}